import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import {
  ComponentCtaButton,
  EnumCtaButtonStyleType,
  EnumCtaButtonVariant,
} from '$/(cms)/_modules/cmsService/types';
import Button, { ButtonStyleType, ButtonVariant } from '$/components/Button';

export const gqlCtaStyleTypeToButtonStyleType: Record<
  EnumCtaButtonStyleType,
  ButtonStyleType
> = {
  [EnumCtaButtonStyleType.Primary]: 'primary',
  [EnumCtaButtonStyleType.Secondary]: 'secondary',
  [EnumCtaButtonStyleType.Neutral]: 'neutral',
  [EnumCtaButtonStyleType.NeutralLightAndBlue]: 'neutral-light-and-blue',
  [EnumCtaButtonStyleType.Cta]: 'cta',
};

export const gqlCtaVariantToButtonVariant: Record<
  EnumCtaButtonVariant,
  ButtonVariant
> = {
  [EnumCtaButtonVariant.Filled]: 'filled',
  [EnumCtaButtonVariant.Outline]: 'outline',
  [EnumCtaButtonVariant.Text]: 'text',
};

export interface CallToActionButtonProps {
  cta: ComponentCtaButton;
  onClick?: () => void;
  iconRight?: ReactNode;
  className?: string;
  index?: number;
}

const resolveVariant = (
  cta: ComponentCtaButton,
  index: number,
): ButtonVariant => {
  if (cta.variant) {
    return gqlCtaVariantToButtonVariant[cta.variant];
  }

  return index === 0 ? 'filled' : 'outline';
};

const resolveStyleType = (
  cta: ComponentCtaButton,
  index: number,
): ButtonStyleType => {
  if (cta.styleType) {
    return gqlCtaStyleTypeToButtonStyleType[cta.styleType];
  }

  return index === 0 ? 'cta' : 'neutral';
};

export const resolveRel = (cta: ComponentCtaButton) => {
  if (cta.rel) return cta.rel;
  if (cta.target?.toLowerCase() === '_blank') {
    return 'noopener noreferrer';
  }

  return undefined;
};

export const CallToActionButton: FC<CallToActionButtonProps> = (props) => {
  const { cta, className, onClick, iconRight, index = 0 } = props;

  const resolvedVariant = cta.variant ? resolveVariant(cta, index) : 'filled';
  const resolvedStyleType = cta.styleType
    ? resolveStyleType(cta, index)
    : 'cta';
  const resolvedRel = cta.rel ? resolveRel(cta) : undefined;
  const resolvedClassName = classNames('w-full', className);

  if (cta.link) {
    return (
      <Button
        as="a"
        href={cta.link}
        target={cta.target}
        rel={resolvedRel}
        variant={resolvedVariant}
        styleType={resolvedStyleType}
        className={resolvedClassName}
        onClick={onClick}
      >
        {cta.label}
        {iconRight}
      </Button>
    );
  }

  return (
    <Button
      variant={resolvedVariant}
      styleType={resolvedStyleType}
      className={resolvedClassName}
      onClick={onClick}
    >
      {cta.label}
      {iconRight}
    </Button>
  );
};
